import { Colors, type Color } from '@ptml/design-system';
import {
    RobotSignalGradientDirectionList,
    RobotSignalVariantList,
    type RobotSignalGradientDirection,
    type RobotSignalVariant,
    type RobotSignalVariantToken,
} from './RobotSignal.types';

/**
 * RobotSignal-branded tokens: the tri-color gradient constants and the
 * variant token map consumed by RobotSignal.styles.ts and RobotSignal.tsx.
 */

// ---------------------------------------------------------------------------
// Gradient color constants
// ---------------------------------------------------------------------------

type RgbColor = { r: number; g: number; b: number };

const HEX_RADIX = 16;

function hexToRgb(hex: string): RgbColor {
    return {
        r: parseInt(hex.slice(1, 3), HEX_RADIX),
        g: parseInt(hex.slice(3, 5), HEX_RADIX),
        b: parseInt(hex.slice(5, 7), HEX_RADIX),
    };
}

function toRgba(
    { r: red, g: green, b: blue }: RgbColor,
    alpha: number,
): string {
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/**
 * Single source of truth for the RobotSignal brand gradient's 2 stop colors, sourced from
 * Colors.Orderly. Matches Figma's `linear-gradient(91deg, #6C3CFA, #FF681D)`.
 */
const ROBOT_SIGNAL_GRADIENT_COLORS = {
    CORAL: Colors.Orderly.Sunset50,
    PURPLE: Colors.Orderly.Mauve60,
} as const satisfies Record<string, string>;

const { CORAL, PURPLE } = ROBOT_SIGNAL_GRADIENT_COLORS;

/** Solid stop colors, e.g. for an SVG `<linearGradient>`'s `<stop>` elements. */
export const ROBOT_SIGNAL_GRADIENT_STOPS = {
    start: CORAL,
    end: PURPLE,
} as const;

/** Default direction for the border/tint gradients, matching the original fixed `90deg` look. */
const DEFAULT_GRADIENT_DIRECTION =
    RobotSignalGradientDirectionList.LEFT_TO_RIGHT;

/** Solid gradient - used for container border rings. */
export function getRobotSignalBorderGradient(
    direction: RobotSignalGradientDirection = DEFAULT_GRADIENT_DIRECTION,
) {
    return `linear-gradient(
        ${direction},
        ${ROBOT_SIGNAL_GRADIENT_STOPS.start} 0%,
        ${ROBOT_SIGNAL_GRADIENT_STOPS.end} 100%
    )`;
}

/** 10%-opacity gradient - used for the checkbox-row card's interior tint wash. */
const TINT_ALPHA = 0.1;

export function getRobotSignalTintGradient(
    direction: RobotSignalGradientDirection = DEFAULT_GRADIENT_DIRECTION,
) {
    return `linear-gradient(
        ${direction},
        ${toRgba(hexToRgb(CORAL), TINT_ALPHA)} 0%,
        ${toRgba(hexToRgb(PURPLE), TINT_ALPHA)} 100%
    )`;
}

const ROBOT_SIGNAL_HEADER_GRADIENT_COLORS = {
    CORAL: '#FF7A6B',
    PINK: '#FF5C8A',
    PURPLE: '#8B5CF6',
} as const satisfies Record<string, string>;

const HEADER_TINT_ALPHA = 0.08;

export function getRobotSignalHeaderGradient() {
    const { CORAL: coral, PINK: pink, PURPLE: purple } =
        ROBOT_SIGNAL_HEADER_GRADIENT_COLORS;

    return `linear-gradient(
        93deg,
        ${toRgba(hexToRgb(coral), HEADER_TINT_ALPHA)} 0%,
        ${toRgba(hexToRgb(pink), HEADER_TINT_ALPHA)} 30.29%,
        ${toRgba(hexToRgb(purple), HEADER_TINT_ALPHA)} 76.44%
    )`;
}

// ---------------------------------------------------------------------------
// Variant tokens
// ---------------------------------------------------------------------------

/** SVG gradient def id the icon's `color`/`fill` references for a gradient fill. */
export const ROBOT_SIGNAL_GRADIENT_ID = 'robot-signal-icon-gradient';

/** Fill reference for gradient variants - requires `<RobotSignalGradientDefs />` mounted once nearby. */
const GRADIENT_ICON_FILL = `url(#${ROBOT_SIGNAL_GRADIENT_ID})` as Color;

export const RobotSignalVariantTokens: Record<
    RobotSignalVariant,
    RobotSignalVariantToken
> = {
    [RobotSignalVariantList.GRADIENT_CARD]: {
        background: (direction) => `
            ${getRobotSignalTintGradient(direction)} padding-box,
            linear-gradient(${Colors.White}, ${Colors.White}) padding-box,
            ${getRobotSignalBorderGradient(direction)} border-box
        `,
        disabledBackground: Colors.Grey05,
        iconColor: Colors.Orderly.Gunmetal100,
        disabledIconColor: Colors.Grey50,
        textColor: Colors.Orderly.Mauve90,
        disabledTextColor: Colors.Grey50,
    },
    [RobotSignalVariantList.GRADIENT_OUTLINE]: {
        // No tint layer: interior stays white, only the border shows the gradient.
        background: (direction) => `
            linear-gradient(${Colors.White}, ${Colors.White}) padding-box,
            ${getRobotSignalBorderGradient(direction)} border-box
        `,
        disabledBackground: Colors.Grey05,
        iconColor: GRADIENT_ICON_FILL,
        disabledIconColor: Colors.Grey50,
        textColor: Colors.Orderly.Mauve90,
        disabledTextColor: Colors.Grey50,
    },
    [RobotSignalVariantList.GRADIENT_ICON_ONLY]: {
        // No border/background at all - just the gradient-filled icon on its own.
        background: () => 'none',
        disabledBackground: 'none',
        iconColor: GRADIENT_ICON_FILL,
        disabledIconColor: Colors.Grey50,
        textColor: Colors.Orderly.Mauve90,
        disabledTextColor: Colors.Grey50,
    },
    [RobotSignalVariantList.GRADIENT_SOLID]: {
        background: (direction = RobotSignalGradientDirectionList.RIGHT_TO_LEFT) =>
            `${getRobotSignalBorderGradient(direction)} border-box`,
        disabledBackground: Colors.Grey05,
        iconColor: Colors.White,
        disabledIconColor: Colors.Grey50,
        textColor: Colors.White,
        disabledTextColor: Colors.Grey50,
    },
    [RobotSignalVariantList.SOLID_DARK]: {
        background: () => `${Colors.Orderly.Gunmetal80} border-box`,
        disabledBackground: Colors.Grey05,
        iconColor: Colors.White,
        disabledIconColor: Colors.Grey50,
        textColor: Colors.White,
        disabledTextColor: Colors.Grey50,
    },
    [RobotSignalVariantList.SOLID_PURPLE]: {
        background: () => `${Colors.Orderly.Mauve70} border-box`,
        disabledBackground: Colors.Grey05,
        iconColor: Colors.White,
        disabledIconColor: Colors.Grey50,
        textColor: Colors.White,
        disabledTextColor: Colors.Grey50,
    },
};
